import {
	Component,
	computed,
	DestroyRef,
	inject,
	signal,
	type OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { Invoice } from '../../../models/invoice';
import { EFilingHttpService } from '../../../services/e-filing-http.service';
import { type OcrSuggestions } from '../../../models/ocr/ocr-suggestion';
import { ToastService } from '../../../services/toast.service';
import { type InvoiceLineItem } from '../../../models/invoice-line-item';
import { environment } from '../../../../../environments/environment';
import { EmbarkSpinnerComponent } from '../../embark-spinner/embark-spinner.component';
import { OcrSuggestionsComponent } from '../../ocr-suggestions/ocr-suggestions.component';

import { CurrencyPipe, DatePipe, NgTemplateOutlet } from '@angular/common';

import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Chip } from 'primeng/chip';
import { Select } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { Timeline } from 'primeng/timeline';
import { Tag } from 'primeng/tag';
import { type InvoiceDialogData } from '../../../models/invoice-dialog-data';
import { type InvoiceAttachment } from '../../../models/invoice-attachment';
import { PdfSelectionComponent } from './pdf-selection/pdf-selection.component';
import { catchError, finalize, tap, throwError, type Observable } from 'rxjs';
import { RequisitionerStore } from '../../../stores/requisitioner-store';
import { type InvoiceDetails } from '../../../models/invoice-details';
import { type OpenInvoiceJournal } from '../../../models/enverus/openInvoice/open-invoice-journal';
import { MsalService } from '@azure/msal-angular';
import { resolveActiveAccount } from '../../../guards/active-account';
import { userHasTestAccess } from '../../../guards/e-filing.guard';
import { type OcrResponse } from '../../../models/ocr/ocr-response';
import { type OpenTicketJournal } from '../../../models/enverus/openTicket/open-ticket-journal';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';

//handles the state/logic for the detail popover that takes shows when an invoice is clicked on the dashboard
@Component({
	selector: 'app-invoice-detail',
	templateUrl: './invoice-detail.component.html',
	styleUrls: ['./invoice-detail.component.sass'],
	providers: [DialogService, ConfirmationService],
	imports: [
		TableModule,
		Button,
		Chip,
		Select,
		FormsModule,
		CurrencyPipe,
		DatePipe,
		NgTemplateOutlet,
		EmbarkSpinnerComponent,
		OcrSuggestionsComponent,
		Timeline,
		Tag,
		ConfirmDialog
	]
})
export class InvoiceDetailComponent implements OnInit {
	public readonly invoiceDetails = signal<InvoiceLineItem[]>([]);
	public loading = true;
	public invoice!: Invoice;
	public subtotal: number | null = null;
	public environment = environment;
	public invoiceUrl = '';
	public salesOrderUrl = '';
	public files: InvoiceAttachment[] = [];
	protected readonly parseFloat = parseFloat;
	private eFilingHttpService = inject<EFilingHttpService>(EFilingHttpService);
	private dialogConfig = inject<DynamicDialogConfig>(DynamicDialogConfig);
	private toastService = inject<ToastService>(ToastService);
	private dialogService = inject<DialogService>(DialogService);
	protected readonly requisitionerStore =
		inject<RequisitionerStore>(RequisitionerStore);
	private msalService = inject<MsalService>(MsalService);
	private readonly destroyRef = inject(DestroyRef);
	private confirmationService =
		inject<ConfirmationService>(ConfirmationService);
	public readonly invoiceJournals = signal<OpenInvoiceJournal[]>([]);
	public oiHistoryExpanded = false;
	public readonly ticketJournals = signal<OpenTicketJournal[]>([]);
	public otHistoryExpanded = false;
	public submissionHistoryExpanded = false;
	public ticketComplianceExpanded = false;
	public requisitionerUpdating = false;

	// current dropdown selection vs. the value the invoice loaded with
	public readonly selectedRequisitioner = signal('');
	private originalRequisitioner = '';
	public readonly requisitionerChanged = computed(
		() => this.selectedRequisitioner() !== this.originalRequisitioner
	);

	public readonly sortedJournals = computed(() =>
		[...this.invoiceJournals()].sort(
			(a, b) =>
				new Date(b.actionDate).getTime() -
				new Date(a.actionDate).getTime()
		)
	);

	public readonly sortedTicketJournals = computed(() =>
		[...this.ticketJournals()].sort(
			(a, b) => b.actionSequence - a.actionSequence
		)
	);

	public onSubmit: ((invoice: Invoice) => void) | undefined;

	public get hasRequisitionerError(): boolean {
		const hasError =
			this.invoice.errors?.some(
				(w) => w.code === 'RequisitionerUnmatched'
			) ?? false;

		const awaitingSubmission = !this.invoice.otStatus;

		return hasError && awaitingSubmission;
	}

	public get requisitionerErrorResolved(): boolean {
		if (!this.hasRequisitionerError) return true;

		return !!this.invoice.enverusRequisitioner;
	}

	public fetchOcrSuggestions = (_refresh = false): Observable<OcrResponse> =>
		this.eFilingHttpService.getSuggestions(this.invoice, _refresh);

	public get ocrCurrentValues(): Record<string, string> {
		const inv = this.invoice as unknown as Record<string, unknown>;
		const toString = (v: unknown): string => {
			const isPrimitive =
				typeof v === 'string' ||
				typeof v === 'number' ||
				typeof v === 'boolean';
			return isPrimitive ? String(v) : '';
		};
		return {
			...Object.fromEntries(
				Object.entries(inv).map(([k, v]) => [k, toString(v)])
			),
			afeLoe: toString(inv['afeloe'])
		};
	}

	public ngOnInit(): void {
		//data coming from the dashboard component
		const data = this.dialogConfig.data as InvoiceDialogData;
		this.invoice = data.invoice;

		this.originalRequisitioner = this.invoice.requisitioner;
		this.selectedRequisitioner.set(this.invoice.requisitioner);

		this.onSubmit = data.onSubmit;
		this.files = data.invoice.files;

		const baseUrl = `https://${ environment.netSuiteInstance.subdomain }.app.netsuite.com/app/accounting/transactions`;

		this.invoiceUrl = `${ baseUrl }/custinvc.nl?id=${ this.invoice.internalId }`;
		this.salesOrderUrl = `${ baseUrl }/salesord.nl?id=${ this.invoice.salesOrderID }`;

		//if there is an invoice available to display, fetch details for it
		if (this.invoice.internalId) {
			this.fetchInvoiceDetails(
				this.invoice.internalId,
				this.invoice.parentCompany
			);
		} else {
			this.loading = false;
		}

		if (
			this.invoice.submissionErrors?.length ||
			this.invoice.submissionWarnings?.length
		) {
			this.submissionHistoryExpanded = true;
		}

		if (this.invoice.ticketComplianceStatus === 'errors') {
			this.ticketComplianceExpanded = true;
		}
	}

	//method that shows/redirects to view the package for the invoice in NetSuite.
	public viewPdf(): void {
		if (!this.files.length) {
			this.toastService.showErrorToast('No attachments found');
			return;
		}

		const baseUrl = `https://${ environment.netSuiteInstance.subdomain }.app.netsuite.com`;

		if (this.invoice.primaryPdf) {
			const url = `${ baseUrl }${ this.invoice.primaryPdf.url }`;
			window.open(url, '_blank');
			return;
		}

		if (this.files.length === 1) {
			const url = baseUrl + this.files[0]?.url;
			window.open(url, '_blank');
			return;
		}

		this.dialogService.open(PdfSelectionComponent, {
			header: 'Attachments',
			width: '480px',
			modal: true,
			closable: true,
			closeOnEscape: true,
			dismissableMask: true,
			data: { files: this.files }
		});
	}

	//method that retrieves invoice detail data from an ID
	private fetchInvoiceDetails(
		internalId: number,
		parentCompany: string
	): void {
		this.loading = true; //show loading spinner

		//call dashboard http service to retrieve the line level detail for an invoice
		this.eFilingHttpService
			.getInvoiceDetails(internalId, parentCompany)
			.subscribe({
				next: (data: InvoiceDetails) => {
					const lines = data.invoiceLines;
					this.invoiceDetails.set(lines); //set the detail data to the response
					this.invoice.invoiceLines = lines;

					if (data.openInvoiceJournals)
						this.invoiceJournals.set(data.openInvoiceJournals);

					if (data.openTicketJournals) {
						this.ticketJournals.set(data.openTicketJournals);
					}
					this.loading = false; // turn off the loader
					this.calculateTotals(); // calculate the totals for the detail popover
				},
				//if there is an error with the http call
				error: () => {
					this.toastService.showErrorToast(
						'Error Fetching Invoice Detail'
					); //show error toast if there is an issue
					this.loading = false; //turn off loader
				}
			});
	}

	//method that calculates the totals for the detail popover
	private calculateTotals(): void {
		let sum = 0;
		for (const lineItem of this.invoiceDetails()) {
			//convert the string into a float
			let floatVal = lineItem.amount;

			//just use 0 if there is an issue with the string we are getting
			if (isNaN(floatVal)) {
				this.toastService.showErrorToast(
					'Invalid amount value in invoice line items'
				);
				floatVal = 0;
			}

			sum += floatVal;
		}

		this.subtotal = sum;
	}

	public doOcrSubmit = (
		changes: OcrSuggestions,
		updateOnly: boolean
	): Observable<Invoice> => {
		this.invoice.isSubmitting = true;
		return this.eFilingHttpService
			.acceptSuggestions(
				this.invoice.internalId,
				this.invoice.parentCompany,
				changes,
				updateOnly
			)
			.pipe(
				tap((invoice: Invoice) => {
					this.invoice = Object.assign(new Invoice(), invoice);
				}),
				catchError((err: unknown) => {
					this.toastService.showErrorToast('Error updating Invoice');
					return throwError(() => err);
				}),
				finalize(() => {
					this.invoice.isSubmitting = false;
				})
			);
	};

	public logFromTicketPayload(): void {
		this.eFilingHttpService
			.getFromTicketPayload(this.invoice.invoiceId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (payload) => {
					console.log('[FromTicket payload]', payload);
					this.toastService.showSuccessToast(
						'Payload logged to console'
					);
				},
				error: () => {
					this.toastService.showErrorToast(
						'Error fetching FromTicket payload'
					);
				}
			});
	}

	public confirmReset(invoice: Invoice): void {
		this.confirmationService.confirm({
			key: 'resetInvoice',
			header: 'Reset Invoice',
			message:
				'This action will reset open ticket status and open invoice status in NetSuite. This will also remove any application records of the associated records in open ticket/open invoice. This action should only be taken if the invoice or ticket in open ticket/open invoice has been manually deleted.',
			acceptLabel: 'Yes',
			rejectLabel: 'No',
			accept: () => {
				this.invoice.isSubmitting = true;
				this.eFilingHttpService
					.resetInvoice(invoice.internalId, invoice.parentCompany)
					.pipe(
						takeUntilDestroyed(this.destroyRef),
						finalize(() => {
							this.invoice.isSubmitting = false;
						})
					)
					.subscribe({
						next: (updatedInvoice: Invoice) => {
							const updated = Object.fromEntries(
								Object.entries(updatedInvoice).filter(
									([, value]) =>
										value !== undefined && value !== null
								)
							);
							Object.assign(this.invoice, updated);

							this.toastService.showSuccessToast(
								'Invoice reset successfully'
							);
						},
						error: () => {
							this.toastService.showErrorToast(
								'Error resetting invoice'
							);
						}
					});
			}
		});
	}

	public canTicketFlip(): boolean {
		return (
			this.invoice.otStatus?.toLowerCase() === 'approved' &&
			!this.invoice.oiStatus &&
			userHasTestAccess(resolveActiveAccount(this.msalService))
		);
	}

	public manualTicketFlip(): void {
		const ticketListItem: Record<string, unknown> = {
			itemID: this.invoice.openTicketId?.toString() ?? '',
			receiptNumber: this.invoice.ticketNumber,
			status: 'APPROVED',
			displayStatus: 'APPROVED',
			lastActionDateTime: new Date().toISOString()
		};

		this.eFilingHttpService
			.manualTicketFlip(ticketListItem)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					this.toastService.showSuccessToast(
						'Ticket flipped successfully'
					);
				},
				error: () => {
					this.toastService.showErrorToast('Error flipping ticket');
				}
			});
	}
}
